import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSchoolConfig } from '../utils/domainConfig';
import { 
  FaBars, 
  FaTimes, 
  FaUserCircle, 
  FaClock, 
  FaCalendarAlt,
  FaHome,
  FaChartLine,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaClipboardCheck,
  FaLayerGroup,
  FaClipboardList,
  FaChartBar,
  FaCog,
  FaChevronDown,
  FaChevronRight,
  FaSun,
  FaWhatsapp,
  FaBus,
  FaRupeeSign,
  FaShieldAlt,
  FaComments,
  FaToolbox,
  FaArrowUp,
  FaSlidersH,
  FaBalanceScale,
  FaBullhorn,
  FaBell,
  FaImages,
} from 'react-icons/fa';
import '../styles/Header.css';

// Helper function to format date and time
const formatDateTime = () => {
  const now = new Date();
  const options = { 
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  };
  return now.toLocaleString('en-US', options).split(',');
};

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [currentTime, setCurrentTime] = useState(formatDateTime());
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const schoolConfig = getSchoolConfig();

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(formatDateTime());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowLogoutConfirm(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target) && !event.target.closest('.burger-menu')) {
        setMenuOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleLogoutClick = () => {
    setShowLogoutConfirm(!showLogoutConfirm);
  };

  const handleLogout = async () => {
    try {
      setShowLogoutConfirm(false);
      const { error } = await signOut();
      
      if (error) {
        console.error('Logout error:', error);
      }
      
      navigate('/login', { 
        replace: true,
        state: { from: 'logout' }
      });
    } catch (error) {
      console.error('Error during logout:', error);
      navigate('/login', { replace: true });
    }
  };
  
  const handleCancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const location = useLocation();
  
  // State for expanded/collapsed menu items
  const [expandedItems, setExpandedItems] = useState({
    academic: false,
    settings: false,
    utility: false
  });
  
  const [darkMode, setDarkMode] = useState(() => {
    // Check for saved preference, default to light theme
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark'; // Only use dark if explicitly set
  });
  
  // Apply theme class to body
  useEffect(() => {
    if (darkMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.setAttribute('data-theme', 'light');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);
  
  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  const toggleItem = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Define all possible menu items with icons, categories, and role-based visibility
  const menuCategories = [
    {
      id: 'main',
      label: 'MAIN',
      items: [
        { 
          path: '/home', 
          label: 'Home',
          icon: <FaHome className="menu-icon" />,
        },
        {
          path: '/student/payments',
          label: 'My Payments',
          icon: <FaRupeeSign className="menu-icon" />,
          roles: ['student']
        },
        { 
          path: '/dashboard', 
          label: 'Dashboard', 
          icon: <FaChartLine className="menu-icon" />,
          roles: ['admin']
        },
        {
          id: 'academic',
          label: 'Academic',
          icon: <FaUserGraduate className="menu-icon" />,
          isCategory: true,
          roles: ['admin', 'office_staff'],
          items: [
            { 
              path: '/students', 
              label: 'Students',
              icon: <FaUserGraduate className="menu-icon" />,
              roles: ['admin', 'office_staff']
            },
            { 
              path: '/staffs', 
              label: 'Staffs',
              icon: <FaChalkboardTeacher className="menu-icon" />,
              roles: ['admin', 'office_staff']
            },
            { 
              path: '/classes', 
              label: 'Classes',
              icon: <FaLayerGroup className="menu-icon" />,
              roles: ['admin', 'office_staff']
            },
            { 
              path: '/academic/fees', 
              label: 'Fees',
              icon: <FaRupeeSign className="menu-icon" />,
              roles: ['admin', 'office_staff']
            }
          ]
        },
        {
          id: 'announcement',
          label: 'Announcement',
          icon: <FaBullhorn className="menu-icon" />,
          isCategory: true,
          roles: ['admin', 'office_staff'],
          items: [
            { 
              path: '/announcement/bulk-notice', 
              label: 'Bulk Notice',
              icon: <FaWhatsapp className="menu-icon" />,
              roles: ['admin', 'office_staff']
            },
            { 
              path: '/announcement/chats', 
              label: 'Chats',
              icon: <FaComments className="menu-icon" />,
              roles: ['admin', 'office_staff']
            },
            { 
              path: '/announcement/insights', 
              label: 'Template Insights',
              icon: <FaChartLine className="menu-icon" />,
              roles: ['admin']
            },
            { 
              path: '/announcement/notification-tester', 
              label: 'Notification Tester',
              icon: <FaBell className="menu-icon" />,
              roles: ['admin']
            },
            { 
              path: '/announcement/gallery', 
              label: 'Gallery',
              icon: <FaImages className="menu-icon" />,
              roles: ['admin']
            }
          ]
        },
        { 
          path: '/attendance', 
          label: 'Attendance',
          icon: <FaClipboardCheck className="menu-icon" />,
          roles: ['admin', 'teacher', 'office_staff']
        },
        { 
          path: '/vehicles/track', 
          label: 'Track Vehicles',
          icon: <FaBus className="menu-icon" />,
          roles: ['admin', 'office_staff']
        },
        { 
          path: '/exams', 
          label: 'Exams',
          icon: <FaClipboardList className="menu-icon" />,
          roles: ['admin', 'office_staff']
        },
        { 
          path: '/results', 
          label: 'Results',
          icon: <FaChartBar className="menu-icon" />,
          roles: ['admin', 'office_staff']
        },
        {
          id: 'utility',
          label: 'Utility',
          icon: <FaToolbox className="menu-icon" />,
          isCategory: true,
          roles: ['admin', 'office_staff'],
          items: [
            { 
              path: '/utility/student-promotion', 
              label: 'Student Promotion',
              icon: <FaArrowUp className="menu-icon" />,
              roles: ['admin', 'office_staff']
            },
            { 
              path: '/utility/system-properties', 
              label: 'System Properties',
              icon: <FaSlidersH className="menu-icon" />,
              roles: ['admin']
            },
            { 
              path: '/utility/reconciliation', 
              label: 'Reconciliation',
              icon: <FaBalanceScale className="menu-icon" />,
              roles: ['admin']
            }
          ]
        },
        { 
          path: '/privacy-policy', 
          label: 'Privacy Policy',
          icon: <FaShieldAlt className="menu-icon" />,
        }
      ]
    },
    {
      id: 'settings',
      label: 'SETTINGS',
      items: [
        { 
          path: '/settings', 
          label: 'Settings',
          icon: <FaCog className="menu-icon" />,
          roles: ['admin', 'office_staff']
        }
      ]
    }
  ];



  // Filter menu items based on user role.
  // For nested category items, we must RETURN A NEW ITEM with its sub-items
  // already filtered — otherwise the renderer iterates the original sub-list
  // and shows entries the user is not allowed to see.
  const filterItemsByRole = (items) => {
    return items.reduce((acc, item) => {
      // If item has no roles, allow for everyone
      const hasAccess = !item.roles ? true : (user && item.roles.includes(user.role));
      if (!hasAccess) return acc;

      // Category with sub-items: filter children recursively and drop the
      // category entirely if nothing is visible underneath.
      if (item.items) {
        const visibleSubItems = filterItemsByRole(item.items);
        if (visibleSubItems.length === 0) return acc;
        acc.push({ ...item, items: visibleSubItems });
        return acc;
      }

      acc.push(item);
      return acc;
    }, []);
  };

  // Filter menu categories and items based on user role
  const filteredMenuCategories = menuCategories.map(category => ({
    ...category,
    items: filterItemsByRole(category.items)
  })).filter(category => category.items.length > 0); // Remove empty categories

  return (
    <header className="universal-header">
      <div className="header-content">
        <div className="header-left">
          <div className={`burger-menu ${menuOpen ? 'open' : ''}`} onClick={toggleMenu}>
            {menuOpen ? <FaTimes /> : <FaBars />}
          </div>
          <div className="school-logo-text">
            <h1>{schoolConfig.schoolName}</h1>
            <span className="subtitle">{schoolConfig.subtitle}</span>
          </div>
        </div>
        
        <div className="header-center">
          <div className="datetime-display">
            <div className="date-section">
              <FaCalendarAlt className="icon" />
              <span className="day">{currentTime[0]}</span>
              <span className="date">{currentTime.slice(1, 3).join(',')}</span>
            </div>
            <div className="time-section">
              <FaClock className="icon" />
              <span className="time">{currentTime[3]}</span>
            </div>
          </div>
        </div>
        
        <div className="user-section">
          {user ? (
            <div className="user-details" ref={dropdownRef}>
              <span className="welcome-text">Welcome, {user.Name || (user.email ? user.email.split('@')[0] : 'User')}</span>
              <div className="user-avatar" onClick={handleLogoutClick}>
                {user.Name ? user.Name.charAt(0).toUpperCase() : user.email ? user.email.charAt(0).toUpperCase() : <FaUserCircle />}
              </div>
              {showLogoutConfirm && (
                <div className="logout-confirm">
                  <p>Are you sure you want to logout?</p>
                  <div className="logout-buttons">
                    <button className="confirm-button" onClick={handleLogout}>Logout</button>
                    <button className="cancel-button" onClick={handleCancelLogout}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button 
              className="login-button login-homepage" 
              onClick={() => navigate('/login')}
            >
              Login
            </button>
          )}
        </div>
      </div>

      <div className={`side-menu ${menuOpen ? 'open' : ''}`} ref={menuRef}>
        <nav className="menu-nav">
          {filteredMenuCategories.map((category, catIndex) => (
            <div key={catIndex} className="menu-category">
              <div className="category-header">
                <h3>{category.label}</h3>
              </div>
              <ul>
                {category.items.map((item, itemIndex) => {
                  const isActive = location.pathname === item.path || 
                                 (item.items && item.items.some(subItem => 
                                   location.pathname === subItem.path
                                 ));
                  
                  if (item.isCategory) {
                    return (
                      <li 
                        key={`${catIndex}-${itemIndex}`} 
                        className={`menu-item ${isActive ? 'active' : ''}`}
                      >
                        <div 
                          className="category-item"
                          onClick={() => toggleItem(item.id)}
                        >
                          <span className="menu-item-content">
                            {item.icon}
                            <span>{item.label}</span>
                          </span>
                          {expandedItems[item.id] ? 
                            <FaChevronDown className="dropdown-arrow" /> : 
                            <FaChevronRight className="dropdown-arrow" />
                          }
                        </div>
                        <ul className={`submenu ${expandedItems[item.id] ? 'open' : ''}`}>
                          {item.items.map((subItem, subIndex) => (
                            <li 
                              key={`${catIndex}-${itemIndex}-${subIndex}`}
                              className={`submenu-item ${location.pathname === subItem.path ? 'active' : ''}`}
                            >
                              <Link 
                                to={subItem.path}
                                onClick={() => setMenuOpen(false)}
                                className="submenu-link"
                              >
                                <span className="menu-item-content">
                                  {subItem.icon}
                                  <span>{subItem.label}</span>
                                </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                    );
                  }
                  
                  return (
                    <li 
                      key={`${catIndex}-${itemIndex}`} 
                      className={`menu-item ${isActive ? 'active' : ''}`}
                    >
                      <Link 
                        to={item.path} 
                        onClick={() => setMenuOpen(false)}
                        className="menu-link"
                      >
                        <span className="menu-item-content">
                          {item.icon}
                          <span>{item.label}</span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          
          <div className="theme-toggle" onClick={toggleTheme}>
            <div className="theme-toggle-content">
              <FaSun className="theme-icon" />
              <div className="theme-switch">
                <span className={!darkMode ? 'active' : ''}>Light</span>
                <div className="switch">
                  <div 
                    className="switch-handle" 
                    style={{ transform: darkMode ? 'translateX(20px)' : 'translateX(0)' }}
                  ></div>
                </div>
                <span className={darkMode ? 'active' : ''}>Dark</span>
              </div>
            </div>
          </div>
        </nav>
      </div>
      
      {menuOpen && <div className="menu-overlay" onClick={toggleMenu}></div>}
    </header>
  );
}

export default Header;
